import {Outlet} from "react-router-dom";
import {useTrustCenterConfig} from "./config.ts";
import {withSuspense} from "../state/withSuspense";
import {useFavicon} from "../hooks/favicon";
import {useState} from "react";
import {useClientAccount} from "./hooks/clientAccount";
import ExtendTheme from "../theme/ExtendTheme";
import baseTheme from "./baseTheme";
import {AuthButton} from "./TopNav/Auth.tsx";
import {Box, Button, Flex, FormControl, HStack, Input, Text} from "@chakra-ui/react";
import DocumentTitle from "../components/DocumentTitle.tsx";
import {useTrustCenterPassword} from "./hooks/accessPassword.ts";
import externalApi from "../api/external";
import {useValidatedPromiseState, useValidation} from "../hooks/validationState.ts";
import {HTTPError} from "../api";
import {invalidateQueries, q, useQueryData} from "../state";
import {useExternalLoginDone} from "./hooks/accessToken";
import {unreachableCase} from "../utils/typescript";

const AccessibleTrustCenter = withSuspense(() => {
  const config = useTrustCenterConfig();
  const title = `${config.name} Trust Center | Powered by Platformed`.trim();

  useFavicon(config.favicon_url);

  return (
    <>
      <DocumentTitle title={title} platformedPrefix={false} />
      <ExtendTheme extension={baseTheme}>
        <ExtendTheme extension={config.chakraTheme ?? {}}>
          <Outlet />
        </ExtendTheme>
      </ExtendTheme>
    </>
  );
});

const PasswordLogin = withSuspense(() => {
  const clientAccount = useClientAccount();
  const [_savedPassword, setSavedPassword] = useTrustCenterPassword();
  const [password, setPassword] = useState("");
  const [_validationErrors, setValidationErrors] = useValidation(useState({}), {password});

  const [loggingInWithPassword, loginWithPassword] = useValidatedPromiseState(
    async () => {
      try {
        await externalApi.trustCenters.getConfig(clientAccount, password);
        setSavedPassword(password);
        await invalidateQueries([q.external.filter()]);
      } catch (ex) {
        if (ex instanceof HTTPError) {
          switch (ex.response.status) {
            case 401:
              throw new Error("Incorrect password");
            default:
              break;
          }
        }
        throw ex;
      }
    },
    [password, clientAccount, setSavedPassword],
    setValidationErrors,
  );

  return (
    <Flex justifyContent={"center"} alignItems={"center"} width={"100vw"} height={"100vh"}>
      <Box as="form" onSubmit={loginWithPassword} maxWidth="480px" display={"flex"} marginBottom={20}>
        <FormControl>
          <HStack>
            <Text>Password:</Text>
            <Input
              value={password}
              onChange={e => setPassword(e.target.value)}
              data-testid="tc-password-field"
              borderColor={loggingInWithPassword.lastError ? "red" : ""}
            />
            <Button flex="0 0 auto" type="submit" isDisabled={loggingInWithPassword.inProgress} mx={4}>
              Submit
            </Button>
          </HStack>
        </FormControl>
      </Box>
    </Flex>
  );
});

const LoadTrustCenter = withSuspense(() => {
  useExternalLoginDone();

  const clientAccount = useClientAccount();
  const missingAuth = useQueryData(q.external.trustCenterMissingAuth(clientAccount));

  switch (missingAuth) {
    case null:
      return <AccessibleTrustCenter />;
    case "missing_authorization":
      return (
        <Flex width={"100vw"} height={"80vh"} justifyContent={"center"} alignItems="center" gap={4}>
          <Text>Your trust center is not live. Please log in to view it.</Text>
          <AuthButton buttonType="Login" colorScheme="blue">
            Login
          </AuthButton>
        </Flex>
      );
    case "incorrect_email_or_password":
      return <PasswordLogin />;
    default:
      unreachableCase(missingAuth);
  }
});

export default LoadTrustCenter;
